import { z } from 'zod';
import { LOAN_TYPES } from '../constants/document-types';
import { SignerPolicySchema } from './signer-policy.dto';

// ============================================================================
// TERM SHEET TYPES
// ============================================================================

/**
 * Supported loan types for term sheets
 */
export const LoanType = {
  FIX_AND_FLIP: LOAN_TYPES.FIX_AND_FLIP,
  BRIDGE: LOAN_TYPES.BRIDGE,
  DSCR: LOAN_TYPES.DSCR,
  GROUND_UP: LOAN_TYPES.GROUND_UP,
  COMMERCIAL: LOAN_TYPES.COMMERCIAL,
  MULTIFAMILY: LOAN_TYPES.MULTIFAMILY,
} as const;

export type LoanTypeType = (typeof LoanType)[keyof typeof LoanType];

/**
 * Term sheet status
 */
export const TermSheetStatus = {
  DRAFT: 'draft',
  GENERATING: 'generating',
  GENERATED: 'generated',
  SENT: 'sent',
  VIEWED: 'viewed',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  FAILED: 'failed',
  SIGNING_REQUESTED: 'signing_requested',
  SIGNED: 'signed',
} as const;

export type TermSheetStatusType = (typeof TermSheetStatus)[keyof typeof TermSheetStatus];

/** Statuses visible to consumers in the consumer portal. Internal statuses (generating, generated, failed, etc.) are excluded. */
export const CONSUMER_VISIBLE_TERM_SHEET_STATUSES: readonly TermSheetStatusType[] = [
  TermSheetStatus.SENT,
  TermSheetStatus.VIEWED,
  TermSheetStatus.ACCEPTED,
  TermSheetStatus.REJECTED,
  TermSheetStatus.SIGNING_REQUESTED,
  TermSheetStatus.SIGNED,
] as const;

/**
 * Term sheet section types
 */
export const TermSheetSectionType = {
  HEADER: 'header',
  BORROWER_INFO: 'borrower_info',
  PROPERTY_INFO: 'property_info',
  LOAN_TERMS: 'loan_terms',
  ACCEPTANCE: 'acceptance',
  TERMS_AND_CONDITIONS: 'terms_and_conditions',
  CUSTOM: 'custom',
} as const;

export type TermSheetSectionTypeValue = (typeof TermSheetSectionType)[keyof typeof TermSheetSectionType];

// ============================================================================
// TERM SHEET TEMPLATE SCHEMAS
// ============================================================================

/**
 * Term sheet template branding settings
 */
export const TermSheetBrandingSchema = z.object({
  companyName: z.string().min(1).max(200),
  logoUrl: z.string().url().nullable().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#1976d2'),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#dc004e'),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().max(50).optional(),
  contactAddress: z.string().max(500).optional(),
  website: z.string().url().optional(),
});

export type TermSheetBranding = z.infer<typeof TermSheetBrandingSchema>;

/**
 * Term sheet template section configuration
 */
export const TermSheetSectionConfigSchema = z.object({
  id: z.string().uuid().optional(),
  type: z.enum([
    'header',
    'borrower_info',
    'property_info',
    'loan_terms',
    'acceptance',
    'terms_and_conditions',
    'custom',
  ]),
  title: z.string().min(1).max(200),
  content: z.string().max(50000).optional(), // For T&Cs or custom content
  order: z.number().int().min(0).default(0),
  isRequired: z.boolean().default(true),
  isVisible: z.boolean().default(true),
});

export type TermSheetSectionConfig = z.infer<typeof TermSheetSectionConfigSchema>;

/**
 * Create term sheet template input
 */
export const CreateTermSheetTemplateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  branding: TermSheetBrandingSchema,
  validityDays: z.number().int().min(1).max(365).default(7),
  /**
   * @deprecated Issue #1388 — Migrate to `supportedLoanProductIds:
   * z.array(z.string().uuid())` once admin term-sheet templates page (PR #1307)
   * is wired to the per-business `LoanProduct` catalog. The legacy hardcoded
   * enum is preserved for the transition window.
   */
  supportedLoanTypes: z.array(z.enum(['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily'])),
  sections: z.array(TermSheetSectionConfigSchema).optional(),
  termsAndConditions: z.string().max(100000).optional(), // Business legal boilerplate
  isDefault: z.boolean().default(false),
  // Issue #1304 — required signers per template; consumed by #1305 resolver.
  signerPolicy: SignerPolicySchema.optional(),
});

export type CreateTermSheetTemplate = z.infer<typeof CreateTermSheetTemplateSchema>;

/**
 * Update term sheet template input
 */
export const UpdateTermSheetTemplateSchema = CreateTermSheetTemplateSchema.partial();

export type UpdateTermSheetTemplate = z.infer<typeof UpdateTermSheetTemplateSchema>;

/**
 * Term sheet template response
 */
export const TermSheetTemplateSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  branding: TermSheetBrandingSchema,
  validityDays: z.number(),
  supportedLoanTypes: z.array(z.string()),
  sections: z.array(TermSheetSectionConfigSchema),
  termsAndConditions: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  // Issue #1304 — signer policy defined at template level.
  // Nullable for templates created before #1304 lands (backfill-safe).
  signerPolicy: SignerPolicySchema.nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type TermSheetTemplate = z.infer<typeof TermSheetTemplateSchema>;

// ============================================================================
// BORROWER & PROPERTY SCHEMAS
// ============================================================================

/**
 * Borrower information for term sheet
 */
export const BorrowerInfoSchema = z.object({
  principalName: z.string().min(1).max(200),
  entityName: z.string().max(200).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(50).optional(),
  address: z.string().max(500).optional(),
});

export type BorrowerInfo = z.infer<typeof BorrowerInfoSchema>;

/**
 * Property information for term sheet
 */
export const PropertyInfoSchema = z.object({
  address: z.string().min(1).max(500),
  propertyType: z.string().max(100).optional(),
  units: z.number().int().min(1).optional(),
  squareFeet: z.number().min(0).optional(),
});

export type PropertyInfo = z.infer<typeof PropertyInfoSchema>;

// ============================================================================
// LOAN TERMS SCHEMAS
// ============================================================================

/**
 * Loan terms input for term sheet generation
 */
export const LoanTermsInputSchema = z.object({
  loanType: z.enum(['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily']),
  loanAmount: z.number().min(0),
  purchasePrice: z.number().min(0).optional(),
  arv: z.number().min(0).optional(), // After Repair Value
  rehabBudget: z.number().min(0).optional(),
  interestRate: z.number().min(0).max(100), // As percentage (e.g., 12 for 12%)
  originationPoints: z.number().min(0).max(100), // As percentage
  termMonths: z.number().int().min(1).max(360),
  downPayment: z.number().min(0).optional(),
  otherFees: z.number().min(0).optional(),
  targetClosingDate: z.string().datetime().optional(),
});

export type LoanTermsInput = z.infer<typeof LoanTermsInputSchema>;

/**
 * Calculated loan terms for term sheet
 */
export const CalculatedLoanTermsSchema = z.object({
  // Input values
  loanType: z.string(),
  loanAmount: z.number(),
  purchasePrice: z.number().nullable(),
  arv: z.number().nullable(),
  rehabBudget: z.number().nullable(),
  interestRate: z.number(),
  originationPoints: z.number(),
  termMonths: z.number(),
  downPayment: z.number().nullable(),
  otherFees: z.number().nullable(),

  // Calculated values
  ltv: z.number().nullable(), // Loan to Value (Loan Amount / ARV)
  ltc: z.number().nullable(), // Loan to Cost (Loan Amount / (Purchase + Rehab))
  monthlyPayment: z.number(), // Loan Amount × (Rate/12)
  originationFee: z.number(), // Loan Amount × Origination Points
  rehabHoldback: z.number().nullable(), // = Rehab Budget
  cashToClose: z.number(), // Down payment + Origination Fee + Other Fees
});

export type CalculatedLoanTerms = z.infer<typeof CalculatedLoanTermsSchema>;

// ============================================================================
// TERM SHEET GENERATION SCHEMAS
// ============================================================================

/**
 * Generate term sheet input
 */
export const GenerateTermSheetInputSchema = z.object({
  businessId: z.string().uuid(),
  templateId: z.string().uuid().optional(), // Uses default if not provided
  borrower: BorrowerInfoSchema,
  property: PropertyInfoSchema,
  loanTerms: LoanTermsInputSchema,
  customSections: z
    .array(
      z.object({
        title: z.string(),
        content: z.string(),
      }),
    )
    .optional(),
  generatedBy: z.string(),
  caseId: z.string().uuid().optional(), // Issue #1238: Optional Case linkage
});

export type GenerateTermSheetInput = z.infer<typeof GenerateTermSheetInputSchema>;

/**
 * Generate term sheet result
 */
export const GenerateTermSheetResultSchema = z.object({
  success: z.boolean(),
  termSheetId: z.string().uuid(),
  termSheetNumber: z.string(),
  status: z.enum([
    'draft',
    'generating',
    'generated',
    'sent',
    'viewed',
    'accepted',
    'rejected',
    'expired',
    'failed',
    'signing_requested',
    'signed',
  ]),
  documentUrl: z.string().url().optional(),
  expiresAt: z.string().datetime(),
  calculatedTerms: CalculatedLoanTermsSchema,
  error: z.string().optional(),
  generatedAt: z.string().datetime().optional(),
});

export type GenerateTermSheetResult = z.infer<typeof GenerateTermSheetResultSchema>;

/**
 * Term sheet details response
 */
export const TermSheetDetailsSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  termSheetNumber: z.string(),
  templateId: z.string().uuid().nullable(),
  status: z.enum([
    'draft',
    'generating',
    'generated',
    'sent',
    'viewed',
    'accepted',
    'rejected',
    'expired',
    'failed',
    'signing_requested',
    'signed',
  ]),
  documentUrl: z.string().url().nullable(),

  // Content
  branding: TermSheetBrandingSchema,
  borrower: BorrowerInfoSchema,
  property: PropertyInfoSchema,
  calculatedTerms: CalculatedLoanTermsSchema,
  termsAndConditions: z.string().nullable(),

  // Dates
  issueDate: z.string().datetime(),
  expiresAt: z.string().datetime(),
  sentAt: z.string().datetime().nullable(),
  viewedAt: z.string().datetime().nullable(),
  acceptedAt: z.string().datetime().nullable(),
  rejectedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),

  // E-signature
  signatureUrl: z.string().url().nullable(),
  signedAt: z.string().datetime().nullable(),
  signedBy: z.string().nullable(),
  // CRM-side envelope id, populated once sent for e-signature (Issue #1309).
  // Stored on the term_sheet entity record as `esign_envelope_id`; surfaced
  // here so the business portal envelope progress view can poll status by
  // `getSubmission(envelopeId)` without a separate lookup.
  envelopeId: z.string().nullable().optional(),

  // Case linkage (Issue #1238)
  caseId: z.string().uuid().nullable(),
  consumerId: z.string().uuid().nullable(),

  // Signer policy snapshot (Issue #1309 — option 3).
  // Captured at generate time from the chosen template so the cohort that
  // signs this specific term sheet is insulated from any later edits/deletes
  // of the source template. `resolveSigners` prefers this snapshot over a
  // live template lookup when the term sheet id is known.
  signerPolicy: SignerPolicySchema.nullable().optional(),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string(),
});

export type TermSheetDetails = z.infer<typeof TermSheetDetailsSchema>;

// ============================================================================
// TERM SHEET STATUS UPDATE
// ============================================================================

/**
 * Update term sheet status input
 */
export const UpdateTermSheetStatusInputSchema = z.object({
  businessId: z.string().uuid(),
  termSheetId: z.string().uuid(),
  newStatus: z.enum(['sent', 'viewed', 'accepted', 'rejected', 'expired', 'signing_requested', 'signed']),
  updatedBy: z.string(),
  reason: z.string().max(1000).optional(), // For rejection
});

export type UpdateTermSheetStatusInput = z.infer<typeof UpdateTermSheetStatusInputSchema>;

/**
 * Send term sheet input
 */
export const SendTermSheetInputSchema = z.object({
  businessId: z.string().uuid(),
  termSheetId: z.string().uuid(),
  recipientEmail: z.string().email(),
  recipientName: z.string().max(200).optional(),
  subject: z.string().max(500).optional(),
  message: z.string().max(5000).optional(),
  sentBy: z.string(),
  consumerId: z.string().uuid().optional(), // Issue #1083: Snapshot consumer at send time
});

export type SendTermSheetInput = z.infer<typeof SendTermSheetInputSchema>;

/**
 * Send term sheet result
 */
export const SendTermSheetResultSchema = z.object({
  success: z.boolean(),
  termSheetId: z.string().uuid(),
  sentTo: z.string().email(),
  sentAt: z.string().datetime(),
  trackingId: z.string().optional(),
  error: z.string().optional(),
});

export type SendTermSheetResult = z.infer<typeof SendTermSheetResultSchema>;

// ============================================================================
// TERM SHEET LIST/QUERY
// ============================================================================

/**
 * List term sheets input
 */
export const ListTermSheetsInputSchema = z.object({
  businessId: z.string().uuid(),
  status: z
    .enum([
      'draft',
      'generating',
      'generated',
      'sent',
      'viewed',
      'accepted',
      'rejected',
      'expired',
      'failed',
      'signing_requested',
      'signed',
    ])
    .optional(),
  borrowerName: z.string().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  caseId: z.string().uuid().optional(), // Issue #1238: Filter by linked case
  consumerId: z.string().uuid().optional(), // Issue #1083: Filter by consumer
});

export type ListTermSheetsInput = z.infer<typeof ListTermSheetsInputSchema>;

/**
 * List term sheets result
 */
export const ListTermSheetsResultSchema = z.object({
  items: z.array(TermSheetDetailsSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

export type ListTermSheetsResult = z.infer<typeof ListTermSheetsResultSchema>;

// ============================================================================
// TERM SHEET SETTINGS (Per-Business)
// ============================================================================

/**
 * Term sheet settings for business configuration
 */
export const TermSheetSettingsSchema = z.object({
  defaultValidityDays: z.number().int().min(1).max(365).default(7),
  defaultLoanTypes: z
    .array(z.enum(['fix_and_flip', 'bridge', 'dscr', 'ground_up', 'commercial', 'multifamily']))
    .default(['fix_and_flip', 'bridge']),
  autoSendOnGeneration: z.boolean().default(false),
  requireESignature: z.boolean().default(false),
  defaultTermsAndConditions: z.string().max(100000).nullable().default(null),
});

export type TermSheetSettings = z.infer<typeof TermSheetSettingsSchema>;

export const UpdateTermSheetSettingsSchema = TermSheetSettingsSchema.partial();

export type UpdateTermSheetSettings = z.infer<typeof UpdateTermSheetSettingsSchema>;

// ============================================================================
// CALCULATION HELPERS
// ============================================================================

/**
 * Calculate loan metrics from input
 */
export function calculateLoanTerms(input: LoanTermsInput): CalculatedLoanTerms {
  const {
    loanType,
    loanAmount,
    purchasePrice,
    arv,
    rehabBudget,
    interestRate,
    originationPoints,
    termMonths,
    downPayment,
    otherFees,
  } = input;

  // LTV = Loan Amount / ARV
  const ltv = arv && arv > 0 ? (loanAmount / arv) * 100 : null;

  // LTC = Loan Amount / (Purchase Price + Rehab Budget)
  const totalCost = (purchasePrice ?? 0) + (rehabBudget ?? 0);
  const ltc = totalCost > 0 ? (loanAmount / totalCost) * 100 : null;

  // Monthly Payment = Loan Amount × (Rate / 12 / 100)
  // Interest-only calculation
  const monthlyPayment = loanAmount * (interestRate / 100 / 12);

  // Origination Fee = Loan Amount × (Points / 100)
  const originationFee = loanAmount * (originationPoints / 100);

  // Rehab Holdback = Rehab Budget
  const rehabHoldback = rehabBudget ?? null;

  // Cash to Close = Down Payment + Origination Fee + Other Fees
  const cashToClose = (downPayment ?? 0) + originationFee + (otherFees ?? 0);

  return {
    loanType,
    loanAmount,
    purchasePrice: purchasePrice ?? null,
    arv: arv ?? null,
    rehabBudget: rehabBudget ?? null,
    interestRate,
    originationPoints,
    termMonths,
    downPayment: downPayment ?? null,
    otherFees: otherFees ?? null,
    ltv,
    ltc,
    monthlyPayment,
    originationFee,
    rehabHoldback,
    cashToClose,
  };
}

/**
 * Generate term sheet number format: TS-YYYY-NNNN
 *
 * @param sequence - A numeric value (typically timestamp) used to generate unique identifier
 * @returns A formatted term sheet number like "TS-2026-1234"
 *
 * NOTE: Uses modulo 10000 to normalize sequence to 4 digits. This is suitable for
 * moderate volume usage (up to 10,000 term sheets per year before number reuse).
 * For high-volume deployments, consider using database sequence or UUID-based identifiers.
 *
 * The actual term sheet is uniquely identified by its UUID; this number is for
 * human-readable reference purposes only.
 */
export function generateTermSheetNumber(sequence: number): string {
  const year = new Date().getFullYear();
  const normalizedSequence = Math.abs(sequence) % 10000;
  const paddedSequence = normalizedSequence.toString().padStart(4, '0');
  return `TS-${year}-${paddedSequence}`;
}

/**
 * Default term sheet settings
 */
export const DEFAULT_TERM_SHEET_SETTINGS: TermSheetSettings = {
  defaultValidityDays: 7,
  defaultLoanTypes: ['fix_and_flip', 'bridge'],
  autoSendOnGeneration: false,
  requireESignature: false,
  defaultTermsAndConditions: null,
};

// ============================================================================
// TERM SHEET LIST ITEM (snake_case from gRPC)
// ============================================================================

/**
 * Term sheet list item (snake_case format from gRPC responses)
 * Used for list/table views where data comes directly from gRPC
 */
export const TermSheetListItemSchema = z.object({
  id: z.string().uuid(),
  term_sheet_number: z.string(),
  status: z.enum([
    'DRAFT',
    'SENT',
    'VIEWED',
    'ACCEPTED',
    'REJECTED',
    'EXPIRED',
    'GENERATING',
    'GENERATED',
    'FAILED',
    'SIGNING_REQUESTED',
    'SIGNED',
  ]),
  expires_at: z.string().datetime().nullable(),
  borrower: z
    .object({
      principal_name: z.string().nullable(),
      borrower_entity: z.string().nullable(),
    })
    .nullable(),
  property: z
    .object({
      address: z.string().nullable(),
    })
    .nullable(),
  calculated_terms: z
    .object({
      loan_amount: z.number().nullable(),
      interest_rate: z.number().nullable(),
      term_months: z.number().nullable(),
    })
    .nullable(),
  created_at: z.string().datetime().optional(),
  case_id: z.string().uuid().nullable().optional(), // Issue #1238
  consumer_id: z.string().uuid().nullable().optional(), // Issue #1083
});

export type TermSheetListItem = z.infer<typeof TermSheetListItemSchema>;
